import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import ButtonBase from '@mui/material/ButtonBase'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Rating from '@mui/material/Rating'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useTranslation } from 'react-i18next'

import { contentApi } from '../../api/endpoints'
import { useLocale } from '../../i18n/LocaleProvider'
import type { TestimonialDto } from '../../types/api'
import { EXTERNAL_LISTINGS, EXTERNAL_REVIEW_TOTAL, type ExternalListing } from '../../content/presence'

/** Cards on the page; the rest wait in the popup. Six fills a three-column row twice. */
const VISIBLE = 6

/**
 * What guests say - a row of cards, and everything else in a popup.
 *
 * <p>The same shape as GeoSmile's reviews (reviews_ui.dart): equal-height cards, a "See all"
 * that opens a window rather than a page, and the window opening on the card that was clicked.
 * A guest browsing reviews is not navigating away; they should still see the page behind.
 *
 * <p>Two sources, kept visibly apart. The cards are reviews left on this site or typed up by
 * the owner - stored here, published by a person. Below them, the ratings on Google, Airbnb,
 * Booking.com and Tripadvisor, each a link. Those reviews are not copied: they belong to the
 * guests who wrote them and the platforms forbid republishing them. A rating, a count and a
 * link is a fact about the estate and sends the reader to the real thing.
 */
export function GuestReviews({ heading = true }: { heading?: boolean }) {
  const { t } = useTranslation()
  const { locale } = useLocale()
  const [openAt, setOpenAt] = useState<number | null>(null)

  const reviews = useQuery({
    queryKey: ['testimonials', locale],
    queryFn: () => contentApi.testimonials(locale),
  })
  const all = reviews.data ?? []
  const visible = all.slice(0, VISIBLE)

  return (
    <Box>
      {heading && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h2" sx={{ mb: 1 }}>
            {t('about.whatGuestsSay')}
          </Typography>
          <Typography color="text.secondary">{t('about.whatGuestsSayBody')}</Typography>
        </Box>
      )}

      {visible.length > 0 && (
        <>
          <Box
            sx={{
              display: 'grid',
              gap: 2.5,
              // Equal-height cards: grid rows stretch every card to the tallest in the row, so
              // a three-line review beside a seven-line one still reads as one row.
              gridTemplateColumns: {
                xs: '1fr',
                sm: visible.length > 1 ? 'repeat(2, 1fr)' : '1fr',
                md: `repeat(${Math.min(3, visible.length)}, 1fr)`,
              },
              alignItems: 'stretch',
            }}
          >
            {visible.map((review, index) => (
              <ReviewCard key={review.id} review={review} clamp onOpen={() => setOpenAt(index)} />
            ))}
          </Box>

          {all.length > VISIBLE && (
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Button variant="outlined" size="large" onClick={() => setOpenAt(0)}>
                {t('reviews.seeAll', { count: all.length })}
              </Button>
            </Box>
          )}
        </>
      )}

      <Elsewhere />

      <AllReviews reviews={all} openAt={openAt} onClose={() => setOpenAt(null)} />
    </Box>
  )
}

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
}

function ReviewCard({
  review,
  clamp,
  onOpen,
}: {
  review: TestimonialDto
  clamp?: boolean
  onOpen?: () => void
}) {
  const { t } = useTranslation()
  const quoteRef = useRef<HTMLQuoteElement | null>(null)
  const [cut, setCut] = useState(false)

  // Measured, not guessed from the length: the same 300 characters are cut on a phone and fit
  // whole in one wide card. A "read more" that reveals nothing new teaches people to ignore it.
  useEffect(() => {
    const el = quoteRef.current
    if (!clamp || !el) return
    const measure = () => setCut(el.scrollHeight > el.clientHeight + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [clamp, review.quote])

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', height: '100%', display: 'flex' }}>
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
        {review.rating != null && <Rating value={review.rating} max={5} readOnly size="small" />}

        <Typography
          ref={quoteRef}
          component="blockquote"
          sx={{
            m: 0,
            lineHeight: 1.65,
            flex: 1,
            ...(clamp && {
              display: '-webkit-box',
              WebkitLineClamp: 6,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }),
          }}
        >
          {review.quote}
        </Typography>

        {clamp && cut && onOpen && (
          <ButtonBase
            onClick={onOpen}
            sx={{ alignSelf: 'flex-start', color: 'primary.main', fontWeight: 600, fontSize: 14 }}
          >
            {t('reviews.readMore')}
          </ButtonBase>
        )}

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pt: 0.5 }}>
          {/* Initials when there is no photo. A missing photograph looks deliberate; a wrong one
              destroys the trust the section exists to build. */}
          <Avatar src={review.photoUrl ?? undefined} alt={review.authorName} sx={{ width: 40, height: 40 }}>
            {initials(review.authorName)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {review.authorName}
            </Typography>
            {review.authorRole && (
              <Typography variant="caption" color="text.secondary" display="block">
                {review.authorRole}
              </Typography>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}

/**
 * Every review, in a window. Opens scrolled to the card that was clicked: clicking the third
 * review to read it in full and being shown the first is the bug GeoSmile's version fixed.
 */
function AllReviews({
  reviews,
  openAt,
  onClose,
}: {
  reviews: TestimonialDto[]
  openAt: number | null
  onClose: () => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const phone = useMediaQuery(theme.breakpoints.down('sm'))
  const items = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    if (openAt == null || openAt === 0) return
    // After the dialog has laid out its list.
    const id = window.setTimeout(() => {
      items.current[openAt]?.scrollIntoView({ block: 'start', behavior: 'smooth' })
    }, 120)
    return () => window.clearTimeout(id)
  }, [openAt])

  return (
    <Dialog
      open={openAt != null}
      onClose={onClose}
      scroll="paper"
      fullWidth
      maxWidth="md"
      // A window on a desktop, the whole screen on a phone - a dialog inset on a 360 px screen
      // leaves a review a column of four words wide.
      fullScreen={phone}
      aria-labelledby="all-reviews-title"
    >
      <DialogTitle id="all-reviews-title" sx={{ pr: 7 }}>
        {t('about.whatGuestsSay')}
        <Typography variant="body2" color="text.secondary" component="span" display="block">
          {t('reviews.count', { count: reviews.length })}
        </Typography>
        <IconButton aria-label={t('common.close')} onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.default' }}>
        <Stack spacing={2}>
          {reviews.map((review, index) => (
            <Box key={review.id} ref={(el: HTMLDivElement | null) => { items.current[index] = el }}>
              <ReviewCard review={review} />
            </Box>
          ))}
        </Stack>
      </DialogContent>
    </Dialog>
  )
}

function score(listing: ExternalListing) {
  // Booking.com's own scale is out of 10; converting it to stars would invent a number the
  // site never published.
  return listing.scale === 10 ? `${listing.rating.toFixed(1)}/10` : `${listing.rating}★`
}

/** The ratings on other sites, each one a link to the reviews themselves. */
function Elsewhere() {
  const { t } = useTranslation()

  return (
    <Box sx={{ mt: 5 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        {t('reviews.elsewhereTitle')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('reviews.elsewhereBody', { count: EXTERNAL_REVIEW_TOTAL })}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
        }}
      >
        {EXTERNAL_LISTINGS.map((listing) => (
          <Link
            key={listing.url}
            href={listing.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="none"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.75,
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              bgcolor: 'background.paper',
              color: 'text.primary',
              transition: 'border-color 140ms',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Box
              sx={{
                minWidth: 64,
                textAlign: 'center',
                fontWeight: 700,
                fontSize: 17,
                color: 'primary.main',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {score(listing)}
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {listing.platform}
                {listing.listing && (
                  <Typography component="span" variant="body2" color="text.secondary">
                    {' · '}
                    {t(`reviews.listing.${listing.listing}`)}
                  </Typography>
                )}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('reviews.count', { count: listing.count })}
              </Typography>
            </Box>
            <OpenInNewIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
          </Link>
        ))}
      </Box>
    </Box>
  )
}
